import { useMemo } from "react";
import {
  Alert,
  Box,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ImageIcon from "@mui/icons-material/Image";
import { Link as RouterLink } from "react-router-dom";
import { getWarps } from "../api/warps";
import { getPrerenderWarpList } from "../utils/prerenderData";
import { homeWarpsPreviewKey } from "../utils/routeCacheKeys";
import useRouteResource from "../hooks/useRouteResource";
import HomePreviewSkeleton from "./loading/HomePreviewSkeleton";

const PREVIEW_LIMIT = 6;

function getWarpImageUrl(warp) {
  const img = warp.images?.[0];
  if (!img) return null;
  return `/uploads/warps/${img.thumbnailPath || img.originalPath}`;
}

export default function PlayerWarpsPreview() {
  const prerenderWarps = useMemo(
    () => getPrerenderWarpList().slice(0, PREVIEW_LIMIT),
    []
  );

  const {
    data: warps,
    error,
    isReady,
  } = useRouteResource({
    cacheKey: homeWarpsPreviewKey(PREVIEW_LIMIT),
    fetch: async () => {
      const data = await getWarps({ limit: PREVIEW_LIMIT });
      return data.warps || [];
    },
    prerenderFallback: prerenderWarps,
  });

  const warpItems = warps || [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Player Warps
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Discover community destinations across the server — shops, farms, builds, and more.
      </Typography>

      {!isReady ? (
        <HomePreviewSkeleton count={PREVIEW_LIMIT} />
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : warpItems.length === 0 ? (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No player warps yet. Be the first to share a destination.
        </Typography>
      ) : (
        <Grid container spacing={1} sx={{ mb: 2 }}>
          {warpItems.map((warp) => {
            const imageUrl = getWarpImageUrl(warp);
            return (
              <Grid item xs={4} sm={4} md={2} key={warp.id}>
                <Box
                  component={RouterLink}
                  to={`/warps/${warp.id}`}
                  title={warp.name}
                  sx={{
                    display: "block",
                    aspectRatio: "1 / 1",
                    overflow: "hidden",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    "&:hover": { boxShadow: 2 },
                  }}
                >
                  {imageUrl ? (
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={warp.name}
                      loading="lazy"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)",
                      }}
                    >
                      <ImageIcon sx={{ fontSize: 32, color: "rgba(255,255,255,0.4)" }} />
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mt: 1 }}>
        <Link
          component={RouterLink}
          to="/warps"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
        >
          View all warps
          <ArrowForwardIcon fontSize="small" />
        </Link>
        <Link
          component={RouterLink}
          to="/warps/submit"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontWeight: 600 }}
        >
          Submit a warp
          <ArrowForwardIcon fontSize="small" />
        </Link>
      </Box>
    </Box>
  );
}
